import { useEffect, useId, useState } from "react";
import { AiModelSelect } from "@studio-features/meta-crew/llm-model-select";
import { useAiModels } from "../lib/llm-api";
import { Dialog, DialogActions, Field, SegmentedControl, Select } from "@studio";
import { KeywordInput } from "@studio-features/meta-crew/seo-shared";
import { ReviewNotices, ReviewSummary } from "@studio-features/meta-crew/manuscript-review";
import type { ReviewNotice } from "@studio-features/meta-crew/refine-data";
import { can, useMe } from "../lib/access";
import { api, errorMessage } from "../lib/api";
import { taskTypeNames } from "../lib/format";
import { useAction, useCustomers, useDevices, useMachines, useSummary } from "../lib/queries";
import { TenantRoute } from "./review";
import { LibrarySendDialog } from "./manuscripts";

type When = "now" | "at" | "repeat";
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

/** 작업 유형별 입력 (서버 시스템 템플릿의 {{변수}} 와 맞춘다). 블로그 발행은 원고 하나로 받는다 (제목·본문·태그·공개 범위 모두 원고 안). */
const FIELDS: Record<string, { name: string; label: string; multiline?: boolean; placeholder?: string; required?: boolean }[]> = {
  phone_task: [{ name: "instructions", label: "폰에서 할 일", multiline: true, required: true, placeholder: "예: 크롬에서 '오늘 날씨'를 검색하고 첫 결과 제목을 알려줘" }],
  naver_blog_post: [],
  text_task: [{ name: "prompt", label: "요청 내용", multiline: true, required: true }],
  browser_task: [{ name: "instructions", label: "브라우저에서 할 일", multiline: true, required: true }],
};

const MANUSCRIPT_PLACEHOLDER = "---\n태그: 진료 안내\n공개: 전체\n---\n# 글 제목\n\n완성된 본문을 붙여넣으세요.";

export type ManuscriptPreview = {
  title: string;
  settings: { tags: string[]; visibility: "public" | "neighbors" | "private"; category?: string };
  blocks: { type: string; text?: string; files?: { name: string }[]; url?: string }[];
  media: { name: string; video: boolean }[];
  errors: string[];
  warnings: string[];
  /** 자동 검토 (원고함 설계 11절): 가이드라인이 못 막은 것. 등록을 막지 않는다 */
  notices?: ReviewNotice[];
};
export const VISIBILITY_NAMES = { public: "전체", neighbors: "이웃", private: "비공개" } as const;

/** 원고 미리보기: 입력을 멈추면 서버 파서로 확인한다 (등록 때와 같은 규칙. 시안의 클라이언트 검사 대신 코어 파서를 쓴다). */
export function useManuscriptPreview(customerId: string, manuscript: string, fileNames: string[], enabled: boolean) {
  const [preview, setPreview] = useState<ManuscriptPreview | null>(null);
  const names = fileNames.join("|");
  useEffect(() => {
    if (!enabled || !customerId || !manuscript.trim()) {
      setPreview(null);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      api
        .post<ManuscriptPreview>(`/c/${customerId}/tasks/manuscript/preview`, { manuscript, fileNames: names ? names.split("|") : [] })
        .then((r) => live && setPreview(r))
        .catch(() => live && setPreview(null));
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [customerId, manuscript, names, enabled]);
  return preview;
}

function blockText(b: ManuscriptPreview["blocks"][number]): string {
  if (b.type === "media") return `사진·영상: ${(b.files ?? []).map((f) => f.name).join(", ")}`;
  if (b.type === "divider") return "구분선";
  if (b.type === "link") return `링크: ${b.url ?? ""}`;
  return `${b.type === "heading" ? "소제목: " : b.type === "quote" ? "인용: " : ""}${(b.text ?? "").slice(0, 80)}`;
}

/** 시안 06a1acb ManuscriptEditor: 파일 불러오기, 원고, 사진·영상 첨부, 원고 확인(제목·문단·파일·태그·공개·오류·순서). 양식·가이드 다운로드는 서버 경로가 없어 안내 문구로. */
export function ManuscriptEditor({ value, onChange, files, onFiles, preview, formId }: { value: string; onChange: (v: string) => void; files: File[]; onFiles: (f: File[]) => void; preview: ManuscriptPreview | null; formId: string }) {
  const counts = (preview?.blocks ?? []).reduce<Record<string, number>>((acc, b) => ((acc[b.type] = (acc[b.type] ?? 0) + 1), acc), {});
  return (
    <div className="lwd-stack">
      <p className="meta-crew-muted">원고 형식: 맨 위 머리말(태그·공개·카테고리) → # 제목 → 본문. 사진·영상은 ![설명](파일명) 으로 자리를 잡고 같은 이름의 파일을 첨부합니다. 빈 양식과 작성 가이드는 운영팀이 전달합니다.</p>
      <Field
        label="원고 파일 불러오기"
        type="file"
        accept=".md,.markdown,.txt,text/markdown,text/plain"
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) void f.text().then(onChange);
        }}
      />
      <div className="field">
        <label htmlFor={`${formId}-manuscript`}>
          원고<span className="required"> *</span>
        </label>
        <textarea id={`${formId}-manuscript`} aria-label="원고" className="input lwd-manuscript" value={value} onChange={(e) => onChange(e.target.value)} placeholder={MANUSCRIPT_PLACEHOLDER} spellCheck={false} required />
      </div>
      <Field label="사진·영상 첨부 (파일 이름이 원고와 같아야 합니다)" type="file" multiple accept="image/*,video/*" onChange={(e) => onFiles(Array.from(e.currentTarget.files ?? []))} />
      {!!files.length && <p className="meta-crew-muted">{files.map((f) => f.name).join(" · ")}</p>}
      {preview && (
        <section className="lwd-preview" aria-label="원고 확인">
          <strong>{preview.title || "원고 확인"}</strong>
          <p>
            {VISIBILITY_NAMES[preview.settings.visibility]} 공개 · 문단 {counts.paragraph ?? 0}개 · 파일 {preview.media.length}개{preview.media.some((m) => m.video) ? ` (영상 ${preview.media.filter((m) => m.video).length})` : ""} · {preview.settings.category ?? "기본 카테고리"}
          </p>
          <p>태그: {preview.settings.tags.join(", ") || "없음"}</p>
          {preview.errors.length > 0 ? (
            <ul className="lwd-errors" role="alert">
              {preview.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : (
            <p role="status">형식과 첨부 파일이 일치합니다. 등록하면 승인된 원고로 실행합니다.</p>
          )}
          {/* 요청서 29-5절: 검사 칸 "통과 / 오류 n / 경고 n / 검토 n" + 검토 항목 (서버 값 그대로) */}
          <ReviewSummary errors={preview.errors.length} warnings={preview.warnings.length} notices={preview.notices?.length} />
          {!!preview.notices?.length && (
            <details>
              <summary>검토 {preview.notices.length}개 보기</summary>
              <ReviewNotices notices={preview.notices} />
            </details>
          )}
          {preview.warnings.map((w) => (
            <p key={w} className="meta-crew-muted">
              {w}
            </p>
          ))}
          <details>
            <summary>본문 순서 미리보기</summary>
            <ol>
              {preview.blocks.map((b, i) => (
                <li key={i}>{blockText(b)}</li>
              ))}
            </ol>
          </details>
        </section>
      )}
    </div>
  );
}

/**
 * 작업 등록 (시안 CreateTask). 즉시 / 예약 / 반복 → 서버 Job.
 * customerId 가 있으면 그 고객사(실행 고객사 고정) 안에서 등록한다. 없으면 전체 관리에서 여는 경우로, 실행 고객사를 고른다.
 * 고객사 회원(직원 아님)이 열면 "새 발행": 블로그 발행만, 원고·첨부·일시만 받고 고객사 설정에 따라 운영팀 확인 뒤 실행된다 (2026-10-16 고객 발행).
 * resubmitRunId 가 있으면 등록 대신 그 요청의 원고를 다시 제출한다.
 */
export function CreateTaskDialog({
  customerId: fixedCustomerId,
  open,
  onClose,
  onCreated,
  initialManuscript = "",
  resubmitRunId,
}: {
  customerId?: string;
  open: boolean;
  onClose: () => void;
  onCreated: (runId: string | null, customerId: string) => void;
  initialManuscript?: string;
  resubmitRunId?: string;
}) {
  const formId = useId();
  const me = useMe();
  const staff = !!me?.isStaff;
  const selectExecution = !fixedCustomerId && staff;
  const allCustomers = useCustomers("", staff);
  const operableCustomers = (allCustomers.data?.items ?? []).filter((c) => c.status === "active" || c.status === "overdue");
  const [execId, setExecId] = useState(fixedCustomerId ?? "");
  const customerId = fixedCustomerId ?? execId;
  const [type, setType] = useState("naver_blog_post");
  const [keywords, setKeywords] = useState<string[]>([]);
  // 시안 85245ef: SEO 키워드는 직원이 게시 작업에 붙인다 (블로그 발행은 1개 이상 필수, 폰 작업은 선택).
  const canSeo = staff;
  const seoField = canSeo && (type === "naver_blog_post" || type === "phone_task");
  // 시안 6a084bb: 실행 고객사(머신 소유)와 대상 고객사(글이 다루는 곳)를 나눈다. 다르면 크루 작업. 직원만.
  // 2026-10-21 사용자: 대상 고객사는 기본 빈값, 직원이 직접 골라야 등록된다 (실행 고객사를 그대로 넣는 실수 방지).
  const [targetCustomerId, setTargetCustomerId] = useState("");
  // 시안 f1df563 (요청서 17절): 미선택 등록은 버튼을 막지 않고 대상 입력 아래에 오류를 띄워 그 입력으로 보낸다.
  const [targetError, setTargetError] = useState("");
  const targetRequired = canSeo && !resubmitRunId;
  const summary = useSummary(customerId);
  const customersQ = useCustomers("", canSeo);
  const targetName = (customersQ.data?.items ?? []).find((c) => c.id === targetCustomerId)?.name ?? summary.data?.name ?? "";
  const [when, setWhen] = useState<When>("now");
  const [repeat, setRepeat] = useState("daily");
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [manuscript, setManuscript] = useState(initialManuscript);
  const [source, setSource] = useState<"direct" | "library">("direct");
  const isBlog = type === "naver_blog_post";
  const preview = useManuscriptPreview(customerId, manuscript, files.map((f) => f.name), open && isBlog);
  // 전체 공개 글만 검색에 나오므로 SEO 키워드는 전체 공개 발행에서만 필수 (비공개·이웃 공개는 순위를 추적하지 않음).
  const publicPost = (preview?.settings.visibility ?? "public") === "public";
  const seoRequired = canSeo && isBlog && publicPost;
  // API 키 방식에서 쓸 AI 모델 (직원만 고름, 고객은 못 바꿈). 로컬 세션 머신에서는 쓰이지 않는다.
  const llmModels = useAiModels(staff && open);
  const [llmModel, setLlmModel] = useState("");
  const devices = useDevices(staff ? customerId : "");
  const machines = useMachines(staff ? customerId : "");
  // 2026-10-21 사용자: 실행 고객사를 잘못 골라 PC 없는 곳에 등록돼 멈춘 일. 연결된 PC가 없으면 등록 전에 알린다.
  const noMachine = staff && !!customerId && !!machines.data && !machines.data.items.some((m) => m.pairedAt && m.status !== "retired");
  const create = useAction(
    (body: Record<string, unknown>): Promise<{ id?: string; run?: { id: string } | null }> =>
      resubmitRunId ? api.post<{ id?: string }>(`/c/${customerId}/runs/${resubmitRunId}/resubmit`, body) : api.post<{ run: { id: string } | null }>(`/c/${customerId}/tasks`, body),
    [["c", customerId], ["admin", "runs"], ["admin", "overview"], ["run"]],
  );
  useEffect(() => {
    if (open) {
      setError(null);
      setFiles([]);
      setManuscript(initialManuscript);
      setKeywords([]);
      setTargetCustomerId("");
      setTargetError("");
      if (!staff) setType("naver_blog_post");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // 전체 관리에서 실행 고객사를 처음 고르면 첫 번째 정상 고객사. 대상 고객사는 따라가지 않는다 (직원이 고른다).
  useEffect(() => {
    if (selectExecution && open && !execId && operableCustomers[0]) setExecId(operableCustomers[0].id);
  }, [selectExecution, open, execId, operableCustomers]);

  const reportTargetError = () => {
    setTargetError("대상 고객사를 고르세요.");
    document.getElementById(formId)?.querySelector<HTMLSelectElement>('[name="targetCustomerId"]')?.focus();
  };
  const submit = async (form: HTMLFormElement) => {
    const data = new FormData(form);
    const params: Record<string, unknown> = {};
    for (const f of FIELDS[type] ?? []) {
      const v = String(data.get(f.name) ?? "").trim();
      if (v) params[f.name] = v;
    }
    if (isBlog) {
      if (!manuscript.trim()) throw new Error("원고를 입력하세요.");
      if (preview?.errors.length) throw new Error(`원고를 확인하세요: ${preview.errors[0]}`);
      params.manuscript = manuscript;
    }
    if (!customerId) throw new Error("실행 고객사를 고르세요.");
    if (seoRequired && keywords.length === 0 && !resubmitRunId) throw new Error("발행 작업은 SEO 키워드를 1개 이상 추가해 주세요.");
    const target = String(data.get("target") ?? "auto");
    const body: Record<string, unknown> = { type, title: String(data.get("taskTitle") ?? "").trim() || undefined, params };
    if (staff) {
      if (seoField && keywords.length && (!isBlog || publicPost)) body.keywords = keywords;
      if (canSeo && targetCustomerId && targetCustomerId !== customerId) body.targetCustomerId = targetCustomerId;
      if (target.startsWith("device:")) body.deviceSerial = target.slice(7);
      if (target.startsWith("machine:")) body.machineId = target.slice(8);
      if (llmModel && !resubmitRunId) body.llmModel = llmModel;
    }
    if (files.length) {
      const uploaded = [];
      for (const f of files) uploaded.push(await api.upload<{ id: string }>(`/c/${customerId}/files`, f));
      body.attachments = uploaded.map((u) => ({ fileId: u.id }));
    }
    if (!resubmitRunId) {
      if (when === "at") {
        body.runAt = new Date(String(data.get("runAt"))).toISOString();
      } else if (when === "repeat" && staff) {
        const [h, m] = String(data.get("time") || "09:00").split(":").map(Number);
        body.repeatCron = repeat === "daily" ? `${m} ${h} * * *` : `${m} ${h} * * ${repeat.slice(5)}`;
        body.timezone = "Asia/Seoul";
        body.name = body.title ?? taskTypeNames[type];
      }
    }
    const r = await create.mutateAsync(body);
    onCreated(resubmitRunId ? (r.id ?? resubmitRunId) : (r.run?.id ?? null), customerId);
    onClose();
  };

  const phoneCapable = type !== "text_task";
  const approval = summary.data?.publishApproval ?? "staff";
  const title = resubmitRunId ? "원고 다시 보내기" : staff ? "작업 등록" : "새 발행";
  // 시안 ae6ed08: 블로그 발행의 원고 입력 방법 = 직접 입력 / 원고함에서 선택 (직원, 원고함 권한)
  const librarySource = staff && isBlog && !resubmitRunId && !initialManuscript && can(me, "MANUSCRIPT_READ");
  if (librarySource && source === "library" && me)
    return (
      <LibrarySendDialog
        open={open}
        me={me}
        initialCustomer={targetCustomerId || customerId || undefined}
        initialExecution={customerId || undefined}
        onExecutionChange={selectExecution ? setExecId : undefined}
        onDirect={() => setSource("direct")}
        onClose={() => {
          setSource("direct");
          onClose();
        }}
      />
    );
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={title}
      description={
        resubmitRunId
          ? "고친 원고와 첨부를 다시 보냅니다. 운영팀이 다시 확인합니다."
          : staff
            ? canSeo
              ? "실행 고객사의 디바이스에 배정하고, 대상 고객사 기준으로 원고와 SEO 키워드를 관리합니다."
              : "이 고객사의 머신과 디바이스에만 배정됩니다."
            : approval === "auto"
              ? "형식에 맞는 원고는 접수 즉시 발행을 시작합니다."
              : "운영팀이 원고를 확인한 뒤 발행을 진행합니다. 진행 상황은 작업 목록에서 볼 수 있습니다."
      }
      size="wide"
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose }}
          primary={{ label: create.isPending ? "보내는 중" : resubmitRunId ? "다시 보내기" : staff ? "작업 등록" : "발행 요청", type: "submit", form: formId, disabled: create.isPending || ((!targetRequired || !!targetCustomerId) && isBlog && !!preview?.errors.length) }}
          note={error ?? undefined}
        />
      }
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          // 대상 미선택이면 대상 오류부터 (원고 검사보다 먼저)
          if (targetRequired && !targetCustomerId) return reportTargetError();
          submit(e.currentTarget).catch((er) => setError(errorMessage(er)));
        }}
      >
        {staff && !resubmitRunId && (
          <Select label="작업 유형" value={type} onChange={(e) => setType(e.target.value)}>
            {["naver_blog_post", "phone_task", "text_task"].map((t) => (
              <option key={t} value={t}>
                {taskTypeNames[t]}
              </option>
            ))}
          </Select>
        )}
        {canSeo && !resubmitRunId && (
          <>
            <div className="lwp-form-grid">
              {selectExecution ? (
                <Select
                  label="실행 고객사"
                  value={execId}
                  onChange={(e) => {
                    setExecId(e.target.value);
                  }}
                >
                  {(allCustomers.data?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id} disabled={!(c.status === "active" || c.status === "overdue")}>
                      {c.name}
                      {c.status === "suspended" || c.status === "terminated" ? " · 등록 불가 (정지·해지)" : ""}
                    </option>
                  ))}
                </Select>
              ) : (
                <Field label="실행 고객사" value={summary.data?.name ?? ""} readOnly />
              )}
              <div className="field">
                <Select
                  label="대상 고객사"
                  name="targetCustomerId"
                  aria-required="true"
                  aria-invalid={!!targetError}
                  aria-describedby={targetError ? `${formId}-target-error` : !targetCustomerId ? `${formId}-target-hint` : undefined}
                  value={targetCustomerId}
                  onChange={(e) => {
                    setTargetCustomerId(e.target.value);
                    setTargetError("");
                  }}
                >
                  <option value="">선택하세요</option>
                  {(customersQ.data?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id} disabled={!(c.status === "active" || c.status === "overdue")}>
                      {c.name}
                      {c.id === customerId ? " · 실행 고객사와 동일" : ""}
                    </option>
                  ))}
                </Select>
                {targetError && (
                  <span className="field-error" id={`${formId}-target-error`} role="alert">
                    {targetError}
                  </span>
                )}
              </div>
            </div>
            {noMachine && (
              <p role="alert" className="seo-notice">
                실행 고객사 {summary.data?.name ?? ""}에 연결된 PC가 없습니다. 이대로 등록하면 PC가 연결될 때까지 배정 대기로 남습니다. 다른 고객사의 PC로 돌리려면 실행 고객사를 그 PC가 있는 곳으로 고르세요.
              </p>
            )}
            {targetCustomerId ? (
              <>
                <TenantRoute executor={{ name: summary.data?.name ?? "" }} subject={{ name: targetName }} crew={targetCustomerId !== customerId} />
                <p className="meta-crew-muted">키워드와 SEO 결과는 {targetName} 소유입니다. 실행 고객사와 대상 고객사가 다르면 크루 작업으로 표시됩니다.</p>
              </>
            ) : (
              <p className="meta-crew-muted" id={`${formId}-target-hint`}>대상 고객사를 골라야 등록할 수 있습니다. 원고와 SEO 성과가 그 고객사 소유가 됩니다.</p>
            )}
          </>
        )}
        {!resubmitRunId && <Field name="taskTitle" label={staff ? "작업 제목 (내부 관리용)" : "제목 (비우면 원고 제목)"} placeholder={isBlog ? "비우면 원고 제목" : "목록에 보일 이름"} maxLength={200} />}
        {librarySource && (
          <SegmentedControl
            label="원고 입력 방법"
            value="direct"
            onChange={(v) => v === "library" && setSource("library")}
            options={[
              { value: "direct", label: "직접 입력" },
              { value: "library", label: "원고함에서 선택" },
            ]}
          />
        )}
        {isBlog && <ManuscriptEditor value={manuscript} onChange={setManuscript} files={files} onFiles={setFiles} preview={preview} formId={formId} />}
        {isBlog && preview && !publicPost && (
          <p role="status" className="seo-notice">
            {VISIBILITY_NAMES[preview.settings.visibility]} 공개 글은 검색에 노출되지 않아 SEO 키워드를 저장하거나 순위를 추적하지 않습니다.
          </p>
        )}
        {(FIELDS[type] ?? []).map((f) =>
          f.multiline ? (
            <div className="field" key={`${type}-${f.name}`}>
              <label htmlFor={`${formId}-${f.name}`}>
                {f.label}
                {f.required && <span className="required"> *</span>}
              </label>
              <textarea id={`${formId}-${f.name}`} name={f.name} required={f.required} rows={5} className="input" placeholder={f.placeholder} />
            </div>
          ) : (
            <Field key={`${type}-${f.name}`} name={f.name} label={f.label} required={f.required} placeholder={f.placeholder} />
          ),
        )}
        {seoField && !resubmitRunId && (isBlog && !publicPost ? null : <KeywordInput value={keywords} onChange={setKeywords} />)}
        {staff && !resubmitRunId && <AiModelSelect models={llmModels.data} value={llmModel} onChange={setLlmModel} />}
        {phoneCapable && !isBlog && <Field label="첨부 파일 (사진·영상은 폰 갤러리로 전달)" type="file" multiple onChange={(e) => setFiles(Array.from(e.currentTarget.files ?? []))} />}
        {staff && phoneCapable && !resubmitRunId ? (
          <Select label="배정" name="target" defaultValue="auto">
            <option value="auto">사용 가능한 디바이스에 자동 배정</option>
            {(machines.data?.items ?? [])
              .filter((m) => m.pairedAt)
              .map((m) => (
                <option key={m.id} value={`machine:${m.id}`}>
                  {m.label} · 이 PC의 아무 폰
                </option>
              ))}
            {(devices.data?.items ?? []).map((d) => (
              <option key={d.id} value={`device:${d.serial}`}>
                {d.machineLabel ?? "미연결"} · {d.alias ?? d.model ?? d.serial}
              </option>
            ))}
          </Select>
        ) : (
          <input type="hidden" name="target" value="auto" />
        )}
        {!resubmitRunId && (
          <>
            <Select label="실행 시점" value={when} onChange={(e) => setWhen(e.target.value as When)}>
              <option value="now">지금</option>
              <option value="at">예약</option>
              {staff && <option value="repeat">반복</option>}
            </Select>
            {when === "at" && <Field name="runAt" label={staff ? "예약 시각" : "희망 발행 시각"} type="datetime-local" required />}
            {when === "repeat" && staff && (
              <>
                <Select label="반복 주기" value={repeat} onChange={(e) => setRepeat(e.target.value)}>
                  <option value="daily">매일</option>
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={`week:${i}`}>
                      매주 {d}요일
                    </option>
                  ))}
                </Select>
                <Field name="time" label="실행 시간 (한국 시간)" type="time" defaultValue="09:00" required />
              </>
            )}
          </>
        )}
      </form>
    </Dialog>
  );
}
